/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Plus, Trash2, Edit2, History } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/accounting`;
const fmt = (n) => (Number(n) || 0).toLocaleString('az-AZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₼';

export default function BuildingsTab() {
  const [buildings, setBuildings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [historyBuilding, setHistoryBuilding] = useState(null);
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payBuilding, setPayBuilding] = useState(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [b, p] = await Promise.all([
        axios.get(`${API}/buildings`, { headers }),
        axios.get(`${API}/building-payments`, { headers }),
      ]);
      setBuildings(b.data || []);
      setPayments(p.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const totalPaidByBuilding = (id) =>
    payments.filter(p => p.building_id === id).reduce((s, p) => s + p.final_amount, 0);

  const lastPaymentForBuilding = (id) => {
    const list = payments.filter(p => p.building_id === id);
    if (!list.length) return null;
    return list.sort((a, b) => b.period_month.localeCompare(a.period_month))[0];
  };

  const deleteBuilding = async (id) => {
    if (!window.confirm('Bina deaktiv edilsin?')) return;
    try {
      await axios.delete(`${API}/buildings/${id}`, { headers });
      toast.success('Deaktiv edildi');
      fetchAll();
    } catch (e) { toast.error('Xəta'); }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Yüklənir...</div>;

  return (
    <div className="space-y-4" data-testid="buildings-tab">
      <div className="bg-white rounded-xl border shadow-sm">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2"><Building2 className="w-4 h-4" /> Binalar ({buildings.length})</h3>
          <Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-building-btn">
            <Plus className="w-4 h-4 mr-1" /> Yeni bina
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-3 py-2">Bina</th>
                <th className="text-left px-3 py-2">Sahibi</th>
                <th className="text-left px-3 py-2">Telefon</th>
                <th className="text-right px-3 py-2">Komissiya</th>
                <th className="text-right px-3 py-2">Cəm ödənilib</th>
                <th className="text-left px-3 py-2">Son ödəniş</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {buildings.length === 0 && (
                <tr><td colSpan="7" className="text-center py-6 text-gray-400">Hələ bina yoxdur</td></tr>
              )}
              {buildings.map(b => {
                const last = lastPaymentForBuilding(b.id);
                return (
                  <tr key={b.id} className="border-t hover:bg-gray-50" data-testid={`building-row-${b.id}`}>
                    <td className="px-3 py-2 font-medium">{b.name}</td>
                    <td className="px-3 py-2">{b.owner_name || '-'}</td>
                    <td className="px-3 py-2 text-xs">{b.owner_phone || '-'}</td>
                    <td className="px-3 py-2 text-right">{b.commission_rate}%</td>
                    <td className="px-3 py-2 text-right font-semibold text-emerald-700">{fmt(totalPaidByBuilding(b.id))}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{last ? `${last.period_month} (${fmt(last.final_amount)})` : '-'}</td>
                    <td className="px-3 py-2 text-right space-x-1">
                      <button onClick={() => setHistoryBuilding(b)} className="text-blue-600" data-testid={`history-building-${b.id}`}>
                        <History className="w-4 h-4 inline" />
                      </button>
                      <Button size="sm" variant="outline" onClick={() => { setPayBuilding(b); setPayModalOpen(true); }} className="text-emerald-700 border-emerald-300" data-testid={`pay-building-${b.id}`}>
                        Ödəniş et
                      </Button>
                      <button onClick={() => { setEditing(b); setModalOpen(true); }} className="text-blue-600">
                        <Edit2 className="w-4 h-4 inline" />
                      </button>
                      <button onClick={() => deleteBuilding(b.id)} className="text-red-500">
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <BuildingModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        editing={editing}
        onSaved={() => { fetchAll(); setModalOpen(false); }}
      />
      <BuildingHistoryModal
        building={historyBuilding}
        onClose={() => setHistoryBuilding(null)}
        payments={payments.filter(p => p.building_id === historyBuilding?.id)}
        onDeleted={fetchAll}
      />
      <PaymentModal
        open={payModalOpen}
        onClose={() => setPayModalOpen(false)}
        building={payBuilding}
        onSaved={() => { fetchAll(); setPayModalOpen(false); }}
      />
    </div>
  );
}

function BuildingModal({ open, onClose, editing, onSaved }) {
  const [form, setForm] = useState({ name: '', address: '', owner_name: '', owner_phone: '', commission_rate: '5.0', note: '' });
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (editing) setForm({
      name: editing.name || '', address: editing.address || '', owner_name: editing.owner_name || '',
      owner_phone: editing.owner_phone || '', commission_rate: String(editing.commission_rate || 5), note: editing.note || '',
    });
    else setForm({ name: '', address: '', owner_name: '', owner_phone: '', commission_rate: '5.0', note: '' });
  }, [editing, open]);

  const save = async () => {
    if (!form.name.trim()) { toast.error('Bina adı tələb olunur'); return; }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = { ...form, commission_rate: parseFloat(form.commission_rate) || 0 };
      if (editing) await axios.put(`${API}/buildings/${editing.id}`, payload, { headers });
      else await axios.post(`${API}/buildings`, payload, { headers });
      toast.success(editing ? 'Yeniləndi' : 'Əlavə edildi');
      onSaved();
    } catch (e) { toast.error('Xəta: ' + (e.response?.data?.detail || e.message)); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>{editing ? 'Binanı redaktə et' : 'Yeni bina'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm">Bina adı</label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="building-name-input" />
          </div>
          <div>
            <label className="text-sm">Ünvan</label>
            <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm">Sahibinin adı</label>
              <Input value={form.owner_name} onChange={e => setForm({ ...form, owner_name: e.target.value })} />
            </div>
            <div>
              <label className="text-sm">Telefon</label>
              <Input value={form.owner_phone} onChange={e => setForm({ ...form, owner_phone: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-sm">Komissiya faizi (%)</label>
            <Input type="number" step="0.1" value={form.commission_rate} onChange={e => setForm({ ...form, commission_rate: e.target.value })} data-testid="building-commission-input" />
          </div>
          <div>
            <label className="text-sm">Qeyd</label>
            <Input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Ləğv</Button>
          <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="building-save-btn">Yadda saxla</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PaymentModal({ open, onClose, building, onSaved }) {
  const [form, setForm] = useState({
    period_month: new Date().toISOString().slice(0, 7),
    collected_amount: '',
    payment_method: 'transfer',
    payment_date: new Date().toISOString().slice(0, 10),
    paid_to_name: '',
    reference: '',
    note: '',
  });
  const [config, setConfig] = useState({ tax_rate: 21, cash_deduction_rate: 10 });
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (open) {
      setForm({
        period_month: new Date().toISOString().slice(0, 7),
        collected_amount: '',
        payment_method: 'transfer',
        payment_date: new Date().toISOString().slice(0, 10),
        paid_to_name: building?.owner_name || '',
        reference: '',
        note: '',
      });
      axios.get(`${API}/config`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setConfig(r.data));
    }
  }, [open, building]);

  if (!building) return null;

  // Live preview
  const collected = parseFloat(form.collected_amount) || 0;
  const taxAmount = collected * config.tax_rate / 100;
  const netAmount = collected - taxAmount;
  const grossCommission = netAmount * building.commission_rate / 100;
  const cashDeduction = form.payment_method === 'cash' ? grossCommission * config.cash_deduction_rate / 100 : 0;
  const finalAmount = grossCommission - cashDeduction;

  const save = async () => {
    if (collected <= 0) { toast.error('Yığılan məbləğ müsbət olmalıdır'); return; }
    try {
      await axios.post(`${API}/building-payments`, {
        building_id: building.id,
        period_month: form.period_month,
        collected_amount: collected,
        payment_method: form.payment_method,
        payment_date: form.payment_date,
        paid_to_name: form.paid_to_name,
        reference: form.reference,
        note: form.note,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Komissiya qeyd edildi');
      onSaved();
    } catch (e) { toast.error('Xəta: ' + (e.response?.data?.detail || e.message)); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>{building.name} — Komissiya Ödənişi</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm">Hesabat dövrü</label>
              <Input type="month" value={form.period_month} onChange={e => setForm({ ...form, period_month: e.target.value })} data-testid="bpay-period-input" />
            </div>
            <div>
              <label className="text-sm">Ödəniş tarixi</label>
              <Input type="date" value={form.payment_date} onChange={e => setForm({ ...form, payment_date: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-sm">Yığılan məbləğ (billing-dən) ₼</label>
            <Input type="number" step="0.01" value={form.collected_amount} onChange={e => setForm({ ...form, collected_amount: e.target.value })} data-testid="bpay-collected-input" />
          </div>
          <div>
            <label className="text-sm">Ödəniş üsulu</label>
            <Select value={form.payment_method} onValueChange={v => setForm({ ...form, payment_method: v })}>
              <SelectTrigger data-testid="bpay-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="transfer">Köçürmə</SelectItem>
                <SelectItem value="cash">Nağd (−10%)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm">Kimə ödənilir</label>
            <Input value={form.paid_to_name} onChange={e => setForm({ ...form, paid_to_name: e.target.value })} />
          </div>
          <div>
            <label className="text-sm">Referans / Qeyd</label>
            <Input value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} placeholder="Bank referans və ya qeyd" />
          </div>

          {/* Live preview */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm space-y-1">
            <p className="font-semibold text-emerald-900 mb-2">Hesablanan ödəniş:</p>
            <div className="flex justify-between"><span>Yığılan:</span><span className="font-mono">{fmt(collected)}</span></div>
            <div className="flex justify-between"><span>Tutum ({config.tax_rate}%):</span><span className="font-mono text-orange-700">−{fmt(taxAmount)}</span></div>
            <div className="flex justify-between border-t pt-1"><span>Net:</span><span className="font-mono">{fmt(netAmount)}</span></div>
            <div className="flex justify-between"><span>Gross komissiya ({building.commission_rate}%):</span><span className="font-mono">{fmt(grossCommission)}</span></div>
            {form.payment_method === 'cash' && (
              <div className="flex justify-between text-amber-700"><span>Nağd tutumu ({config.cash_deduction_rate}%):</span><span className="font-mono">−{fmt(cashDeduction)}</span></div>
            )}
            <div className="flex justify-between border-t pt-1 font-bold text-emerald-900"><span>ÖDƏNƏCƏK:</span><span className="font-mono">{fmt(finalAmount)}</span></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Ləğv</Button>
          <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bpay-save-btn">Yadda saxla</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BuildingHistoryModal({ building, onClose, payments, onDeleted }) {
  const token = localStorage.getItem('token');

  const del = async (id) => {
    if (!window.confirm('Bu ödəniş qeydi silinsin?')) return;
    try {
      await axios.delete(`${API}/building-payments/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Silindi');
      onDeleted();
      onClose();
    } catch (e) { toast.error('Xəta'); }
  };

  if (!building) return null;
  const totalPaid = payments.reduce((s, p) => s + p.final_amount, 0);

  return (
    <Dialog open={!!building} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{building.name} — Tarixçə</DialogTitle>
        </DialogHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-1 text-left">Dövr</th>
                <th className="px-2 py-1 text-right">Yığım</th>
                <th className="px-2 py-1 text-right">Tutum</th>
                <th className="px-2 py-1 text-right">Net</th>
                <th className="px-2 py-1 text-right">Komissiya</th>
                <th className="px-2 py-1">Üsul</th>
                <th className="px-2 py-1">Tarix</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 && (
                <tr><td colSpan="8" className="text-center py-4 text-gray-400">Hələ ödəniş yoxdur</td></tr>
              )}
              {payments.map(p => (
                <tr key={p.id} className="border-t">
                  <td className="px-2 py-1 font-mono text-xs">{p.period_month}</td>
                  <td className="px-2 py-1 text-right">{fmt(p.collected_amount)}</td>
                  <td className="px-2 py-1 text-right text-orange-700">−{fmt(p.tax_amount)}</td>
                  <td className="px-2 py-1 text-right">{fmt(p.net_amount)}</td>
                  <td className="px-2 py-1 text-right font-semibold text-emerald-700">{fmt(p.final_amount)}</td>
                  <td className="px-2 py-1 text-center">{p.payment_method === 'cash' ? '💵' : '💳'}</td>
                  <td className="px-2 py-1 text-xs">{p.payment_date}</td>
                  <td className="px-2 py-1">
                    <button onClick={() => del(p.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 font-bold bg-gray-50">
                <td colSpan="4" className="px-2 py-1">CƏM</td>
                <td className="px-2 py-1 text-right text-emerald-700">{fmt(totalPaid)}</td>
                <td colSpan="3"></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Bağla</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
